import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { buildTimeline } from "../_components/chartData";
import { Countdown } from "../_components/Countdown";
import { OrderBookCompare } from "../_components/OrderBookDisplay";
import PriceChart from "../_components/PriceChart";
import RatesChart from "../_components/RatesChart";
import { getExchangeLink, getExchangeLogo } from "../../shared/exchanges";
import {
  useFundingRateHistories,
  useFundingRates,
  useMarketData,
  usePriceHistories,
} from "../hooks/api";

const ratesSearchSchema = z.object({
  long: z.string().optional(),
  short: z.string().optional(),
});

export const Route = createFileRoute("/rates")({
  validateSearch: ratesSearchSchema,
  component: RatesPage,
});

const fmtCompact = (num?: number) =>
  num === undefined
    ? "-"
    : Intl.NumberFormat("en", {
        notation: "compact",
        maximumSignificantDigits: 4,
      }).format(num);

const signColor = (value: number) =>
  value > 0 ? "text-pos" : value < 0 ? "text-neg" : "text-zinc-400";

// Tailwind's `sm` breakpoint (640px). Below it we default the charts to a
// tighter time window so the last few days are legible on a narrow screen.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

function Stat({
  label,
  loading,
  error,
  value,
  sub,
}: {
  label: string;
  loading: boolean;
  error: boolean;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-wider text-zinc-500 uppercase">
        {label}
      </div>
      {loading ? (
        <div className="mt-1 h-4 w-16 animate-pulse rounded bg-surface-2" />
      ) : error ? (
        <div className="text-sm text-neg">Error</div>
      ) : (
        <>
          <div className="text-sm font-semibold tabular-nums">{value}</div>
          {sub && (
            <div className="text-[11px] text-zinc-500 tabular-nums">{sub}</div>
          )}
        </>
      )}
    </div>
  );
}

function PositionPanel({
  side,
  symbol,
  exchange,
}: {
  side: "long" | "short";
  symbol: string;
  exchange: string;
}) {
  const fundingRates = useFundingRates(exchange, symbol);
  const marketData = useMarketData(exchange, symbol);

  const apr = fundingRates.data?.apr;

  return (
    <div
      className={`flex-1 p-3 md:p-4 ${
        side === "short" ? "pt-6 md:pt-4" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase ${
            side === "long" ? "bg-pos/10 text-pos" : "bg-neg/10 text-neg"
          }`}
        >
          {side}
        </span>
        <img
          src={getExchangeLogo(exchange)}
          alt={exchange}
          className="h-4 w-4 rounded-full object-contain"
        />
        <a
          href={getExchangeLink(exchange, symbol)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm font-semibold text-zinc-100 hover:text-accent"
        >
          {symbol}
          <span className="font-normal text-zinc-500">on {exchange}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-zinc-500"
          >
            <path d="M7 17L17 7" />
            <path d="M8 7h9v9" />
          </svg>
        </a>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-3">
          <Stat
            label="Open Interest"
            loading={marketData.isLoading}
            error={!!marketData.error}
            value={fmtCompact(marketData.data?.openInterestValue)}
          />
          <Stat
            label="Volume 24h"
            loading={marketData.isLoading}
            error={!!marketData.error}
            value={fmtCompact(marketData.data?.quoteVolume)}
          />
        </div>
        <Stat
          label="Next Funding"
          loading={fundingRates.isLoading}
          error={!!fundingRates.error}
          value={
            typeof apr === "number" ? (
              <span className={signColor(apr)}>
                {apr > 0 ? "+" : ""}
                {apr.toFixed(2)}% APR
              </span>
            ) : (
              "-"
            )
          }
          sub={
            fundingRates.data && (
              <>
                <div>{fundingRates.data.rate.toFixed(4)}%</div>
                <div>
                  <Countdown
                    timestamp={fundingRates.data.timestamp.toISOString()}
                    interval={fundingRates.data.fundingInterval}
                  />
                  {" · "}
                  {fundingRates.data.fundingInterval}h
                </div>
              </>
            )
          }
        />
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="w-full">
      <h2 className="mb-1.5 text-[11px] font-semibold tracking-wider text-zinc-500 uppercase">
        {title}
      </h2>
      {children}
    </section>
  );
}

function ChartCard({
  isLoading,
  isError,
  isEmpty,
  emptyMessage,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyMessage: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-edge bg-surface-1 p-3">
      {isLoading ? (
        <div className="h-48 w-full animate-pulse rounded bg-surface-2" />
      ) : isError ? (
        <p className="py-12 text-center text-sm text-neg">
          Error loading chart data
        </p>
      ) : isEmpty ? (
        <p className="py-12 text-center text-sm text-zinc-500">
          {emptyMessage}
        </p>
      ) : (
        children
      )}
    </div>
  );
}

function RatesPage() {
  const { long = "", short = "" } = Route.useSearch();
  const navigate = Route.useNavigate();

  // Parse parameters
  const longSymbol = long.includes("@") ? long.split("@")[0] : "";
  const longExchange = long.includes("@") ? long.split("@")[1] : "";
  const shortSymbol = short.includes("@") ? short.split("@")[0] : "";
  const shortExchange = short.includes("@") ? short.split("@")[1] : "";

  const longHistory = useFundingRateHistories(longExchange, longSymbol);
  const longPriceHistory = usePriceHistories(longExchange, longSymbol);
  const shortHistory = useFundingRateHistories(shortExchange, shortSymbol);
  const shortPriceHistory = usePriceHistories(shortExchange, shortSymbol);

  const isMobile = useIsMobile();
  const visibleDays = isMobile ? 3 : undefined;

  // Shared x-axis timeline across both charts (last 10 days, all datasets).
  // Both charts plot onto the same buckets, which keeps their time scales
  // aligned pixel-for-pixel.
  const chartTimeline = useMemo(() => {
    const datasets = [
      longHistory.data,
      shortHistory.data,
      longPriceHistory.data,
      shortPriceHistory.data,
    ].filter((d) => d !== undefined);
    const timeline = buildTimeline(datasets, 10);
    return timeline.length > 0 ? timeline : undefined;
  }, [
    longHistory.data,
    shortHistory.data,
    longPriceHistory.data,
    shortPriceHistory.data,
  ]);

  // Show error state for invalid parameters
  if (!long || !short || !long.includes("@") || !short.includes("@")) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface-0 px-4 text-zinc-100">
        <h1 className="text-xl font-semibold tracking-tight">
          Invalid Parameters
        </h1>
        <p className="text-sm text-zinc-400">
          Please provide valid exchange and symbol parameters.
        </p>
        <code className="rounded-md border border-edge bg-surface-1 px-3 py-1.5 text-xs text-zinc-400">
          /rates?long=ETHFIUSDT@BINANCE&short=ETHFIUSDC@BINANCE
        </code>
        <Link
          to="/"
          className="mt-2 rounded-md border border-edge bg-surface-1 px-4 py-1.5 text-sm font-medium text-zinc-300 transition-colors hover:bg-surface-2"
        >
          Back to Screener
        </Link>
      </main>
    );
  }

  const handleSwitch = () => {
    void navigate({
      to: "/rates",
      search: { long: short, short: long },
    });
  };

  return (
    <main className="min-h-screen bg-surface-0 text-zinc-100">
      <div className="mx-auto flex max-w-screen-2xl flex-col gap-4 px-2 py-4 md:px-6 md:py-6">
        <header>
          <Link
            to="/opportunities"
            className="mb-1 inline-flex items-center gap-1 text-xs text-zinc-500 transition-colors hover:text-accent"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            Screener
          </Link>
          <h1 className="text-lg font-semibold tracking-tight md:text-xl">
            Funding Rate Analysis
          </h1>
        </header>

        <div className="relative flex flex-col rounded-lg border border-edge bg-surface-1 md:flex-row">
          <PositionPanel
            side="long"
            symbol={longSymbol ?? ""}
            exchange={longExchange ?? ""}
          />
          <div className="border-t border-edge md:border-t-0 md:border-l" />
          <PositionPanel
            side="short"
            symbol={shortSymbol ?? ""}
            exchange={shortExchange ?? ""}
          />
          <button
            onClick={handleSwitch}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-edge bg-surface-1 p-1.5 text-zinc-400 shadow-md transition-colors hover:bg-surface-2 hover:text-accent"
            title="Switch long/short positions"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="rotate-90 md:rotate-0"
            >
              <path d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
          </button>
        </div>

        <Section title={`Funding Rates · last ${visibleDays ?? 10} days`}>
          <ChartCard
            isLoading={longHistory.isLoading || shortHistory.isLoading}
            isError={!!longHistory.error || !!shortHistory.error}
            isEmpty={
              !longHistory.data ||
              !shortHistory.data ||
              !longExchange ||
              !shortExchange
            }
            emptyMessage="No historical data available"
          >
            <RatesChart
              data1={longHistory.data ?? []}
              data2={shortHistory.data ?? []}
              exchange1={longExchange ?? ""}
              exchange2={shortExchange ?? ""}
              syncGroup="rates-page"
              timeline={chartTimeline}
              visibleDays={visibleDays}
            />
          </ChartCard>
        </Section>

        <Section title={`Price · last ${visibleDays ?? 10} days`}>
          <ChartCard
            isLoading={
              longPriceHistory.isLoading || shortPriceHistory.isLoading
            }
            isError={!!longPriceHistory.error || !!shortPriceHistory.error}
            isEmpty={
              !longPriceHistory.data?.length ||
              !shortPriceHistory.data?.length ||
              !longExchange ||
              !shortExchange
            }
            emptyMessage="No historical price data available"
          >
            <PriceChart
              data1={longPriceHistory.data ?? []}
              data2={shortPriceHistory.data ?? []}
              exchange1={longExchange ?? ""}
              exchange2={shortExchange ?? ""}
              symbol1={longSymbol ?? ""}
              symbol2={shortSymbol ?? ""}
              syncGroup="rates-page"
              timeline={chartTimeline}
              visibleDays={visibleDays}
            />
          </ChartCard>
        </Section>

        <Section title="Live Order Book & Spread Matrix">
          {longSymbol && longExchange && shortSymbol && shortExchange ? (
            <OrderBookCompare
              long={{ symbol: longSymbol, exchange: longExchange }}
              short={{ symbol: shortSymbol, exchange: shortExchange }}
            />
          ) : (
            <div className="rounded-lg border border-edge bg-surface-1 p-4">
              <p className="py-12 text-center text-sm text-zinc-500">
                Loading order book data...
              </p>
            </div>
          )}
        </Section>

        <footer className="text-[11px] text-zinc-600">
          <p>
            Chart: scroll or pinch to zoom, drag to pan. Reset Zoom fits the
            full range. Dashed lines show the rate spread moving average.
          </p>
          <p>Spread matrix does not include exchange fees.</p>
        </footer>
      </div>
    </main>
  );
}
